/**
 * Shared SpeechMike command ids and dispatch — used by keyboard (F-key) and
 * Philips Device Connector (PDC) integrations so both paths stay in sync.
 */
import { toast } from "react-toastify";

/** @typedef {(action: () => void | Promise<void>) => Promise<void>} RunMicToggle */

export const PHILIPS_CMD = {
  SIDEBAR_MIC: "sidebar_mic",
  EDITOR_DICTATION: "editor_dictation",
  COMMAND_MODE: "command_mode",
  NEXT_PARAGRAPH: "next_paragraph",
  PREV_PARAGRAPH: "prev_paragraph",
  IMPRESSION: "impression",
  FINDINGS: "findings",
  SAVE: "save",
  PROCESS_AI: "process_ai",
  STOP_DICTATION: "stop_dictation",
};

/**
 * @typedef {Object} PhilipsSpeechMikeCommandOptions
 * @property {boolean} isMicActive
 * @property {boolean} isCommandMode
 * @property {() => void | Promise<void>} toggleEditorDictation
 * @property {() => void | Promise<void>} toggleEditorCommandMode
 * @property {() => void | Promise<void>} toggleSidebarMic
 * @property {() => void | Promise<void>} [stopSidebarMic]
 * @property {() => void} [selectNextParagraph]
 * @property {() => void} [selectPreviousParagraph]
 * @property {(header: string) => void} [selectContentUnderHeader]
 * @property {() => void | Promise<void>} [handleProcessWithAI]
 * @property {boolean} [isWixAuth]
 * @property {boolean} [isCreAuth]
 * @property {() => void | Promise<void>} [handleSave] - Direct save (Radical / non-Wix)
 */

/**
 * @param {string} command
 * @param {PhilipsSpeechMikeCommandOptions} props
 * @param {RunMicToggle} runMicToggle
 */
export async function dispatchPhilipsSpeechMikeCommand(command, props, runMicToggle) {
  switch (command) {
    case PHILIPS_CMD.SIDEBAR_MIC:
      await runMicToggle(() => props.toggleSidebarMic?.());
      return;

    case PHILIPS_CMD.EDITOR_DICTATION:
      await runMicToggle(async () => {
        const willTurnOn = !props.isMicActive;
        toast.info(willTurnOn ? "Dictation: ON" : "Dictation: OFF");
        await props.toggleEditorDictation?.();
      });
      return;

    case PHILIPS_CMD.COMMAND_MODE:
      await runMicToggle(async () => {
        const willTurnOn = !props.isCommandMode;
        toast.info(willTurnOn ? "Command Mode: ON" : "Command Mode: OFF");
        await props.toggleEditorCommandMode?.();
      });
      return;

    case PHILIPS_CMD.STOP_DICTATION:
      if (props.isMicActive) {
        await runMicToggle(async () => {
          toast.info("Dictation: OFF");
          await props.toggleEditorDictation?.();
        });
      } else {
        await props.stopSidebarMic?.();
      }
      return;

    case PHILIPS_CMD.NEXT_PARAGRAPH:
      props.selectNextParagraph?.();
      return;

    case PHILIPS_CMD.PREV_PARAGRAPH:
      props.selectPreviousParagraph?.();
      return;

    case PHILIPS_CMD.IMPRESSION:
      props.selectContentUnderHeader?.("IMPRESSION");
      return;

    case PHILIPS_CMD.FINDINGS:
      props.selectContentUnderHeader?.("FINDINGS");
      return;

    case PHILIPS_CMD.SAVE:
      if (!props.isWixAuth && !props.isCreAuth) {
        void props.handleSave?.();
      }
      return;

    case PHILIPS_CMD.PROCESS_AI:
      await props.handleProcessWithAI?.();
      return;

    default:
      return;
  }
}
